import { useState, useEffect, useContext } from 'react'
import { navigate } from 'gatsby'
import services from 'services'
import entities from 'entities'
import analytics from '@mobi/libraries/analytics'
import { field, formIdRegister } from 'utils/trackings/constants'
import storage from 'utils/storage'
import router from 'utils/router'
import safeStorage from 'utils/safeStorage'
import signin from 'services/signin'
import { ThemeContext } from 'modules/Theme'
import {
  addNRPageView,
  handlePerformanceMark,
} from 'utils/trackings/newRelicEvents'
import removeSpecialChars from '@mobi/utils/formatters/removeSpecialChars'
import analyticsUuid from 'utils/analyticsUuid'
import getAnalyticsContent from 'utils/getAnalyticsContent'
import { useLoader } from 'hooks/useLoader'
import facebook from 'utils/trackings/facebook'
import handleInternalTracking from 'utils/trackings/handleInternalTracking'
import { getSystemToken } from 'utils/systemTokens'
import * as ROUTES from 'Routes'
import featureToggle from 'utils/featureToggle'

const { isCounterOfferActive, isDesenrolaActive } = featureToggle()

const useLandingRegister = ({ location }) => {
  const [birthdayNotValid, setBirthdayNotValid] = useState('')
  const [disableButton, setDisableButton] = useState(false)

  const [formUserInfo, setFormUserInfo] = useState({})
  const [formAutoCompleteValues, setFormAutoCompleteValues] = useState({})

  const { theme } = useContext(ThemeContext)
  const isRecoveryOrigin = theme === 'recovery'

  const { showLoader, hideLoader } = useLoader()

  const formViewedPayload = {
    formId: formIdRegister,
  }

  const formContinuedPayload = {
    formId: formIdRegister,
    field: field,
    stepName: 'Register',
    stepType: 'CREDENTIALS',
  }

  useEffect(async () => {
    const cryptoStore = await safeStorage()

    if (Object.keys(formUserInfo).length) {
      const transformData = JSON.stringify(formUserInfo)

      await cryptoStore?.set('formData', transformData)
    }
  }, [formUserInfo])

  useEffect(async () => {
    const cryptoStore = await safeStorage()
    const formDataStorage = await cryptoStore?.get('formData')

    const values = formDataStorage ? JSON.parse(formDataStorage) : null

    setFormAutoCompleteValues(values)
  }, [])

  useEffect(() => {
    const keepSession = storage.session.getItem('keepSession')
    const callbackUrl = storage.session.getItem('callbackUrl')

    storage.session.removeItem('keepSession')
    if (!keepSession) {
      analyticsUuid.clearKeepingAnalyticsUuid()
    }

    if (!callbackUrl) {
      storage.session.setItem('callbackUrl', window.location.origin)
    }

    analytics.track('formViewed', formViewedPayload)

    addNRPageView('lp_registration')
    analytics.track('gaPageLoad', { pageName: 'PaginadeRegistro' })
  }, [])

  const handleSendSkipper = async ({ accessToken, firstName }) => {
    try {
      const dataSkipper = await services.skipper.getMarketplaceUrls(accessToken)

      storage.session.setItem('marketplaceUrls', dataSkipper)

      handlePerformanceMark(ROUTES.noDebt.pageName)
      navigate('/semDivida', {
        state: { name: firstName },
      })
    } catch (error) {
      handlePerformanceMark(ROUTES.noDebt.pageName)
      navigate('/semDivida', {
        state: { name: firstName },
      })

      console.error('error to send skipper', error)
    }
  }

  const handleRedirectToOffer = async (sessionId, hasDesenrola) => {
    await analytics.track('gaCustomClick', 'registration_completed')

    handleInternalTracking('registration_completed')

    const urlParams = new URLSearchParams(location?.search)
    const useUrlQueryParams = urlParams.get('utm_source') === 'reneg-itau'

    const callbackUrl = storage.local.getItem('callbackUrl')

    const queryParams = useUrlQueryParams
      ? urlParams.toString()
      : `callback_url=${callbackUrl}`

    hideLoader()

    if (hasDesenrola) {
      navigate('/desenrola', {
        state: {
          offerUrl: `/ofertas?sessionId=${sessionId}&theme=${
            isRecoveryOrigin ? 'recovery' : theme
          }&${queryParams}`,
        },
      })
      return
    }

    handlePerformanceMark(ROUTES.ofertas.pageName)
    navigate(
      `/ofertas?sessionId=${sessionId}&theme=${
        isRecoveryOrigin ? 'recovery' : theme
      }&${queryParams}`
    )
  }

  const handleRedirectToCounterOffer = () => {
    analytics.track('gaCustomClick', 'registration_completed')
    handleInternalTracking('registration_completed')

    storage.session.setItem('lead-counter-offer', true)
    handlePerformanceMark(ROUTES.counterOffer.pageName)
    navigate('/contraproposta')
  }

  const handleGetUserAgreements = async ({
    recoveryAuthToken,
    documentNumber,
    birthDate,
    shouldNavigate = true,
  }) => {
    if (!recoveryAuthToken) return

    try {
      const { http } = await signin('additional', true, true)

      if (!http) return

      http.defaults.headers['Token'] = `Bearer ${recoveryAuthToken}`
      http.defaults.headers['SystemToken'] = await getSystemToken()

      const encodedDocumentNumber = btoa(documentNumber)
      const encodedBirthDate = btoa(birthDate)

      const payload = JSON.stringify({
        documentNumber: encodedDocumentNumber,
        birthDate: encodedBirthDate,
      })
      storage.session.setItem('myAgreementsPayload', btoa(payload))

      const response = await http.get(
        `/agreements/?document_number=${encodedDocumentNumber}&birth_date=${encodedBirthDate}`
      )
      const agreementResponse = response?.agreements

      if (!agreementResponse || !agreementResponse?.length) {
        return false
      }

      storage.session.setItem('currentAgreements', agreementResponse)
      handlePerformanceMark(ROUTES.myAgreements.pageName)

      if (shouldNavigate) {
        navigate('/meusacordos')
      }

      return true
    } catch (error) {
      console.error('handle user agreements error--', error)
      return false
    }
  }

  const handleHasDesenrola = (debtGroups) => {
    const hasAtLeastOneDesenrolaGroup = debtGroups?.debtGroups?.some(
      (group) => group?.isDesenrola
    )

    return hasAtLeastOneDesenrolaGroup && isDesenrolaActive
  }

  const handleGetDebtGroups = async ({
    user,
    recoveryAuthToken,
    accessToken,
    firstName,
  }) => {
    try {
      const getUserAgreements = await handleGetUserAgreements({
        recoveryAuthToken,
        documentNumber: user?.document_number,
        birthDate: user?.birth_date,
        userName: user?.name,
        accessToken,
        firstName,
        shouldNavigate: false,
      })

      const debtGroups = await services.debt.getBFFDebtGroups({
        firstName,
        accessToken,
      })
      const sessionId = debtGroups?.sessionId

      const hasDesenrola = handleHasDesenrola(debtGroups)

      if (Object.keys(debtGroups).length) {
        try {
          const utmCampaign = new URLSearchParams(window.location.search).get(
            'utm_campaign'
          )
          const isCounterOfferLead = utmCampaign === 'contraproposta'

          if (isCounterOfferLead && isCounterOfferActive) {
            return handleRedirectToCounterOffer()
          }

          if (!getUserAgreements) {
            return handleRedirectToOffer(sessionId, hasDesenrola)
          }

          return navigate('/meusacordos')
        } catch (e) {
          console.error('error to get user agreements --', e)

          if (!getUserAgreements) {
            return handleRedirectToOffer(sessionId, hasDesenrola)
          }

          return navigate('/meusacordos')
        }
      }
    } catch (error) {
      navigate('/error', {
        state: { name: user?.name },
      })
      console.error('get debt groups error --', error)
    }
  }

  const handleCreateLead = async ({ user }) => {
    try {
      const firstName = user?.name?.split(' ')?.[0]

      storage.session.setItem('lead_data', {
        cellphone: user?.cellphone,
        lead_name: user?.name,
      })

      const recoveryAuth = await signin(null, false)
      const recoveryAuthToken = recoveryAuth?.authData?.token || null

      const response = await services.session.leadIntegradora(user)

      if (!response || !Object.keys(response).length) {
        return navigate('/error')
      }

      const recoveryInfo = response?.recovery
      const renegInfo = response?.reneg
      const accessToken = response?.access_token //This token represents the lead at the level of renegotiations. When we unify the projects, it will be used in all cases
      const refreshToken = response?.refresh_token

      storage.session.setItem('accessToken', accessToken)
      storage.session.setItem('refreshToken', refreshToken)

      if (recoveryInfo?.details) {
        setBirthdayNotValid(recoveryInfo?.details)
        setDisableButton(false)

        analytics.track('gaVisualization', recoveryInfo?.details)

        hideLoader()
        return
      }

      if (!recoveryInfo?.has_debts && !renegInfo?.has_debts) {
        const getUserAgreements = await handleGetUserAgreements({
          recoveryAuthToken,
          documentNumber: user?.document_number,
          birthDate: user?.birth_date,
          userName: user?.name,
          accessToken,
          firstName,
        })

        if (!getUserAgreements) {
          handlePerformanceMark(ROUTES.noDebt.pageName)
          navigate('/semDivida', {
            state: { name: user?.name },
          })

          handleSendSkipper({
            accessToken,
            firstName,
          })

          services.mensageiro.startWorkflow({
            accessToken,
            workflowName: 'has-no-debt-cross-sell-recovery',
          })

          return
        }

        return
      }

      if (renegInfo?.has_debts) {
        const infos = {
          firstName,
          lead_type: user?.lead_type,
          ...renegInfo,
        }

        storage.session.setItem('renegInfo', infos)
      }

      if (!recoveryInfo?.has_debts) {
        const getUserAgreements = await handleGetUserAgreements({
          recoveryAuthToken,
          documentNumber: user?.document_number,
          birthDate: user?.birth_date,
          userName: user?.name,
          accessToken,
          firstName,
        })

        if (getUserAgreements) return

        if (renegInfo?.has_debts && !getUserAgreements) {
          return navigate('/renegociacao-itau')
        }

        return handleSendSkipper({
          accessToken,
          firstName,
        })
      }

      facebook.trackDocumentRegistration()

      recoveryInfo?.token &&
        storage.session.setItem('session_token', recoveryInfo?.token)

      if (recoveryInfo?.session_id) {
        storage.session.setItem('session_id', recoveryInfo?.session_id)
        storage.session.setItem('recovery-session_id', recoveryInfo?.session_id)
      }

      storage.session.setItem('hasRecoveryDebt', recoveryInfo?.has_debts)

      handleGetDebtGroups({
        sessionId: recoveryInfo?.session_id,
        user,
        recoveryAuthToken,
        accessToken,
        firstName,
      })
    } catch (error) {
      console.error('error to create lead', error)

      setDisableButton(false)
      hideLoader()
      return
    }
  }

  const onSubmit = async (values, { source_url }) => {
    showLoader('Consultando dívidas...')
    setDisableButton(true)

    const documentNumber =
      values?.document !== '' ? values?.document : location?.state?.document
    const sourceUrl = location?.state?.source_url || source_url
    const mpUUID = location?.state?.mp_uuid || router.getQueryString('mp_uuid')

    const leadType =
      documentNumber && removeSpecialChars(documentNumber).length > 11
        ? 'PJ'
        : 'PF'

    storage.session.setItem('leadType', leadType)
    storage.session.setItem('startSourceURL', sourceUrl)
    storage.session.setItem('mp_uuid', mpUUID)

    analytics.track('formContinued', formContinuedPayload)
    analytics.track('gaCustomClick', 'document_registration')
    handleInternalTracking('document_registration')

    const userAnalytics = getAnalyticsContent(sourceUrl)

    const formData = {
      document_number: documentNumber,
      analytics: userAnalytics,
      ...values,
    }

    const formattedUser = entities.User(formData)

    const user = {
      ...formattedUser,
      lead_type: leadType,
    }

    handleCreateLead({ user })
  }

  return {
    onSubmit,
    birthdayNotValid,
    disableButton,
    formUserInfo,
    formAutoCompleteValues,
    setBirthdayNotValid,
    setFormUserInfo,
  }
}

export default useLandingRegister
